import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import {
    OrgContextService,
    OrgDomainResolutionFailure,
} from 'app/core/services/org-context.service';
import { TenantContextService } from 'app/core/services/tenant-context.service';

/**
 * Error page a failed tenant-host resolution is parked on. Lives under the
 * guard-free `empty` layout, same as `/errors/org-mismatch`.
 */
export const TENANT_NOT_FOUND_URL = '/errors/tenant-not-found';

/**
 * Tenant-host counterpart to `unknownHostGuard` — app-level error for a
 * hostname that *looks* like a tenant (`{label}.{rootDomain}`) but resolves
 * to no active organization (a deleted/suspended/never-created org's
 * subdomain, which the wildcard ingress still routes to this app).
 *
 * Awaits `OrgContextService`'s in-flight bootstrap resolution rather than
 * issuing its own call, then redirects to
 * `/errors/tenant-not-found?reason=<not-found|suspended|error>` on failure.
 *
 * Wired as `canMatch` after `unknownHostGuard`'s catch-all — 'unknown' hosts
 * are already redirected by then, so only 'tenant' hosts reach this check.
 * The `errors` segment is always let through to avoid an infinite loop.
 */
export const tenantHostGuard: CanMatchFn = async (_route, segments) => {
    if (segments[0]?.path === 'errors') {
        return false;
    }

    // Every inject() must run before the first await — the injection context
    // is only live for the synchronous head of the function (NG0203).
    const tenantContext = inject(TenantContextService);
    const orgContext = inject(OrgContextService);
    const router = inject(Router);

    if (tenantContext.mode !== 'tenant') {
        return false;
    }

    await orgContext.resolveFromDomain();

    const failure: OrgDomainResolutionFailure | null =
        orgContext.domainFailure;
    if (!failure) {
        return false;
    }

    return router.createUrlTree([TENANT_NOT_FOUND_URL], {
        queryParams: { reason: failure },
    });
};
